<script>
  import { isUndefined } from 'lodash';
  import moment from 'moment';
  import { stringify } from 'csv-stringify/browser/esm/sync';
  import { Link } from 'svelte-routing';
  import classNames from 'classnames';
  import {
    Chart,
    Card,
    WidgetPlaceholder,
    Button,
    Spinner,
  } from 'flowbite-svelte';
  import { FileExportSolid } from 'flowbite-svelte-icons';
  import { Heading } from '@button/ui';
  import { formatNumberWithIntl } from '../utils';
  import { queryDailyCuratedStatsByCreator } from '../data/dailyCuratedStats';
  import { sendReportCsvExportDebugEvent } from '../data/debug';
  import { searchParams } from '../store';
  import PercentChange from './PercentChange.svelte';

  const X_AXIS_DATE_FORMAT = 'MMM D';
  const TOTAL_PAGE_VIEWS_COPY = 'Total Page Views';
  const TOTAL_SHORTLINK_CLICKS_COPY = 'Total Shortlink Clicks';
  const TOTAL_PAGE_VIEWS_COLOR = '#1C64F2';
  const TOTAL_SHORTLINK_CLICKS_COLOR = '#16BDCA';
  const exportCopy = 'Export';

  $: creatorStats = queryDailyCuratedStatsByCreator({
    startDate: $searchParams.startDate,
    endDate: $searchParams.endDate,
    socialPlatform: $searchParams.socialPlatform,
  });
  $: currentData = $creatorStats?.isSuccess
    ? $creatorStats.data.currentData
    : {};
  $: previousData = $creatorStats?.isSuccess
    ? $creatorStats.data.previousData
    : {};
  $: currentDailyResults =
    currentData?.daily_results?.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      return dateA - dateB;
    }) || [];
  $: currentTotalPageViews = currentData?.total_page_views;
  $: previousTotalPageViews = previousData?.total_page_views;
  $: currentShortlinkClicks = currentData?.total_shortlink_clicks;
  $: previousShortlinkClicks = previousData?.total_shortlink_clicks;
  $: options = $creatorStats.isSuccess && {
    chart: {
      type: 'area',
      height: '400px',
      maxWidth: '100%',
      fontFamily: 'Inter, sans-serif',
      zoom: false,
      dropShadow: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 2,
      curve: 'straight',
    },
    grid: {
      show: true,
      strokeDashArray: 4,
    },
    series: [
      {
        name: TOTAL_PAGE_VIEWS_COPY,
        data: currentDailyResults.map((daily) => daily.page_views),
        color: TOTAL_PAGE_VIEWS_COLOR,
      },
      {
        name: TOTAL_SHORTLINK_CLICKS_COPY,
        data: currentDailyResults.map((daily) => daily.shortlink_clicks),
        color: TOTAL_SHORTLINK_CLICKS_COLOR,
      },
    ].filter(Boolean),
    xaxis: {
      categories: currentDailyResults.map((daily) =>
        moment(daily.date).format(X_AXIS_DATE_FORMAT),
      ),
      tooltip: {
        enabled: true,
        formatter(value) {
          return moment(currentDailyResults[value - 1]?.date).format(
            X_AXIS_DATE_FORMAT,
          );
        },
      },
      labels: {
        show: true,
        style: {
          fontFamily: 'Inter, sans-serif',
          cssClass: 'text-xs font-normal fill-gray-500',
        },
        formatter(value) {
          const isFirstValue =
            value ===
            moment(currentDailyResults[0]?.date).format(X_AXIS_DATE_FORMAT);
          const isLastValue =
            value ===
            moment(
              currentDailyResults[currentDailyResults.length - 1]?.date,
            ).format(X_AXIS_DATE_FORMAT);

          return isFirstValue || isLastValue ? value : '';
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: [
      {
        seriesName: TOTAL_PAGE_VIEWS_COPY,
        showAlways: true,
        axisBorder: {
          show: true,
          color: TOTAL_PAGE_VIEWS_COLOR,
        },
        labels: {
          formatter(value) {
            return formatNumberWithIntl(value);
          },
        },
      },
      {
        seriesName: TOTAL_SHORTLINK_CLICKS_COPY,
        showAlways: true,
        opposite: true,
        axisBorder: {
          show: true,
          color: TOTAL_SHORTLINK_CLICKS_COLOR,
        },
        labels: {
          formatter(value) {
            return formatNumberWithIntl(value);
          },
        },
      },
    ].filter(Boolean),
    legend: {
      horizontalAlign: 'left',
    },
  };

  $: isLoading = $creatorStats.isLoading;
  $: isError = $creatorStats.isError;
  $: hasNoData =
    $creatorStats.isSuccess &&
    currentShortlinkClicks === 0 &&
    currentTotalPageViews === 0 &&
    previousShortlinkClicks === 0 &&
    previousTotalPageViews === 0;

  function handleClickExport() {
    const mappedResults = currentDailyResults.map((d) => {
      return {
        date: d.date,
        page_views: d.page_views,
        shortlink_clicks: d.shortlink_clicks,
      };
    });

    const csv = stringify(mappedResults, {
      header: true,
      columns: [
        { key: 'date' },
        { key: 'page_views' },
        { key: 'shortlink_clicks' },
      ],
    });

    sendReportCsvExportDebugEvent('curated-total-clicks');

    // Generate a hidden element to trigger file download
    const hiddenElement = window.document.createElement('a');
    hiddenElement.href = `data:text/csv;charset=utf-8,${encodeURI(csv)}`;
    hiddenElement.target = '_blank';
    hiddenElement.download = `daily_curated_clicks_${$searchParams.startDate}_${$searchParams.endDate}.csv`;
    hiddenElement.click();
  }
</script>

<Card size="xl">
  {#if hasNoData}
    <div class="flex h-full flex-col items-center justify-center space-y-6">
      <img src="./images/no-data.svg" alt="No Data" />
      <Heading>No data</Heading>
      <p class="text-gray-500">
        Create a new CuratedBy page and publish it to generate traffic.
      </p>
      <div class="flex gap-4">
        <Link to="/curated-by">
          <Button size="sm">Go to CuratedBy</Button>
        </Link>
      </div>
    </div>
  {/if}
  {#if isLoading}
    <div class="relative">
      <div class="absolute flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
      <WidgetPlaceholder divClass="w-full p-6" />
    </div>
  {/if}
  {#if isError}
    <div class="flex h-full flex-col items-center justify-center">
      <p class="text-gray-500">Something went wrong.</p>
      <button
        class="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        on:click={() => $creatorStats.refetch()}
      >
        Retry
      </button>
    </div>
  {/if}
  <div
    class={classNames({
      hidden: $creatorStats.isLoading || hasNoData,
    })}
  >
    <div class="flex justify-between">
      <div class="flex gap-6">
        {#if !isUndefined(currentTotalPageViews)}
          <Heading class="flex flex-col">
            <span>
              {formatNumberWithIntl(currentTotalPageViews)}
            </span>
            <span class="text-base font-normal text-gray-500"
              >{TOTAL_PAGE_VIEWS_COPY}</span
            >
          </Heading>
        {/if}
        <Heading class="flex flex-col">
          <span>
            {formatNumberWithIntl(currentShortlinkClicks)}
          </span>
          <span class="text-base font-normal text-gray-500">
            {TOTAL_SHORTLINK_CLICKS_COPY}
          </span>
        </Heading>
      </div>
      <div class="hidden xl:block">
        <Button on:click={handleClickExport} size="sm" color="light"
          ><FileExportSolid class="me-2 h-3.5 w-3.5" />{exportCopy}</Button
        >
      </div>
    </div>
    <div>
      {#if options}
        <Chart {options} />
      {/if}
      <div
        class="-mt-2 mb-4 ml-4 flex flex-col gap-2 text-xs xl:absolute xl:bottom-[26px] xl:right-10 xl:flex-row"
      >
        <div>
          <PercentChange
            previous={previousTotalPageViews}
            current={currentTotalPageViews}
            >{TOTAL_PAGE_VIEWS_COPY}</PercentChange
          >
        </div>
        <div>
          <PercentChange
            previous={previousShortlinkClicks}
            current={currentShortlinkClicks}
            >{TOTAL_SHORTLINK_CLICKS_COPY}</PercentChange
          >
        </div>
      </div>
      <div class="block xl:hidden">
        <Button on:click={handleClickExport} size="sm" color="light"
          ><FileExportSolid class="me-2 h-3.5 w-3.5" />{exportCopy}</Button
        >
      </div>
    </div>
  </div>
</Card>
